import { ls, useMkdir } from '../../hooks/useGlobusTransfer';
import { Breadcrumbs, Button, Card, CardContent, Chip, IconButton, Link, Stack, Typography } from '@mui/material';
import LassoGrid from '../../components/LassoGrid';
import { useGlobusAuth } from '@globus/react-auth-context';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { InsertDriveFile } from "@mui/icons-material";
import FolderIcon from "@mui/icons-material/Folder";
import { ColDef } from "@ag-grid-community/core";
import LassoModal from "../../components/LassoModal";
import { LassoTextField } from "../../components/LassoTextField";
import useLocalStorage from '../../hooks/useLocalStorage';
import { selectSelectedStudy } from '../common/databaseSlice';
import { useDispatch, useSelector } from "react-redux";
import {
    globusTransferFiles,
    selectUserCollection,
    setUserCollectionDirectory,
    resetUserCollection,
    UserCollection,
} from "../data-wrangler/globusSlice";
import ComputerIcon from "@mui/icons-material/Computer";

const FileTypeCell = (props: any) => {
    return (
        <Stack height={"100%"} flexDirection={"row"} alignItems={"center"} gap={3}>
            {props.data.type === "dir"
                ? <FolderIcon color={'info'} fontSize="large"/>
                : <InsertDriveFile color={'action'} fontSize="large" />
            }
            <Typography variant={"body1"}>{props.data.name}</Typography>
        </Stack>
    )
}

const sizeFormatter = (bytes: number) => {
    if (bytes === 0) {
        return "0.00 B";
    }

    const e = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, e)).toFixed(2) +
        ' ' + ' KMGTP'.charAt(e) + 'B';
}

const globusCollectionColumnDefs: ColDef[] = [
    {
        field: "name",
        flex: 1,
        cellRenderer: FileTypeCell
    },
    {
        field: "last_modified",
        headerName: "Last Modified",
        valueFormatter: params => {
            if (params.data.type === 'dir') {
                return ""
            } else {
                const date = new Date(params.value);
                return date.toLocaleString();
            }
        }
    },
    {
        field: "size",
        valueFormatter: params => {
            if (params.data.type === 'dir') {
                return ""
            } else {
                return sizeFormatter(params.value);
            }
        },
        width: 150
    }
]

const GlobusGrid = () => {
    const { authorization, isAuthenticated } = useGlobusAuth();
    const mkDir = useMkdir();
    const selectedStudy = useSelector(selectSelectedStudy);
    const userCollection: UserCollection = useSelector(selectUserCollection);
    const { data: myData, isSuccess, isFetching, isError } = useQuery({
        enabled: isAuthenticated && !!userCollection.id,
        retry: false,
        refetchOnWindowFocus: false,
        queryKey: ["globus_collections", userCollection.id, "ls", userCollection.directory],
        queryFn: () => ls(authorization, userCollection.id, userCollection.directory, {}),
    });

    const [fileSelection] = useLocalStorage("globus-file-list", {});
    const [participantFilter] = useLocalStorage("participant-filter", {});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPathModalOpen, setIsPathModalOpen] = useState(false);
    const [newDir, setNewDir] = useState("");
    const [newPath, setNewPath] = useState("");
    const dispatch = useDispatch();
    const workingDirectoryPath = useMemo(() => {
        if (isSuccess && !isFetching) {
            return myData?.absolute_path ? myData?.absolute_path.split("/") : myData?.path.replace(/^\/+/, '/').split("/");
        }
        return [];
    }, [isSuccess, isFetching, myData]);

    const onRowDoubleClicked = (e: any) => {
        const currentDirectory = workingDirectoryPath?.join("/").replace(/^\/+|\/+$/g, '');
        const targetDirectory = e.data.name;
        if(e.data.type === "dir"){
            dispatch(setUserCollectionDirectory(`/${currentDirectory}/${targetDirectory}`));
        }
    }

    const beginTransfer = async () => {
        const pageCount = fileSelection.summary.globusCount / window.env.globusLimit;
        dispatch(globusTransferFiles({
            fileSelection: fileSelection.query,
            participantFilter: participantFilter,
            pageCount: Math.ceil(pageCount),
            page: 0,
            authorization: authorization,
            study: selectedStudy,
            source: selectedStudy.toLowerCase().includes('pre-release') ? window.env.globusHBCD : window.env.globusABCD,
            collection: userCollection.id,
            path: `${userCollection.directory}`
        }))
    }

    const createDirectory = () => {
        const newDirPath = `${userCollection.directory}/${newDir}`;
        mkDir.mutate({ collection: userCollection.id, path: newDirPath });
        dispatch(setUserCollectionDirectory(newDirPath));
        setNewDir("");
        setIsModalOpen(false);
    }

    const goToPath = () => {
        setIsPathModalOpen(false);
        dispatch(setUserCollectionDirectory(newPath));
        setNewPath("");
    }

    const goToRoot = () => {
        dispatch(setUserCollectionDirectory("~"));
        setNewPath("");
    }

    if (isError) {
        return (
            <Card sx={{ width: '60%', paddingY: '1rem', paddingX: '1.5rem', marginInline: 'auto' }}>
                <CardContent>
                    <Typography
                        align="center"
                        sx={{ fontWeight: 600, marginBottom: 3 }}
                        variant="h5"
                    >
                        Directory Listing Failed
                    </Typography>
                    <Typography align="center" sx={{ marginBottom: 3 }} variant="h6">
                        The defined path could not be found in Collection. Please try again.
                    </Typography>
                    <Button onClick={goToRoot}>
                        Go Back to Default Server Path
                    </Button>
                </CardContent>
            </Card>
        )
    }

    return (
        <>
            <Stack height="100%" gap={3}>
                <Stack direction={"row"} gap={3}>
                    <Typography variant={'h6'}>Collection:</Typography>
                    <Chip
                        label={userCollection.label}
                        variant={"outlined"}
                        onDelete={() => dispatch(resetUserCollection())}
                    />
                </Stack>
                <Stack direction={"row"} gap={3} alignItems={"baseline"}>
                    <Breadcrumbs separator="/" sx={{ flexGrow: 1 }}>
                        {workingDirectoryPath?.map((directory, i) =>
                            directory === '' && i === 0
                                ? (
                                    <IconButton key={`icon-${i}`} color="primary" size="small" onClick={() => dispatch(setUserCollectionDirectory("/"))}>
                                        <ComputerIcon />
                                    </IconButton>
                                ) : <Link key={`link-${i}`} href="#" onClick={() => dispatch(setUserCollectionDirectory(workingDirectoryPath.splice(0, i+1).join("/")))}>{directory}</Link>)
                        }
                    </Breadcrumbs>
                    <Button
                        size={"small"}
                        variant={"secondary"}
                        onClick={() => setIsPathModalOpen(true)}
                    >
                        Go to Path
                    </Button>
                    <Button
                        size={"small"}
                        variant={"primary"}
                        onClick={() => setIsModalOpen(true)}
                    >
                        Create New Directory
                    </Button>
                    <Button
                        size={"small"}
                        variant={"primary"}
                        onClick={beginTransfer}
                    >
                        Transfer Here
                    </Button>
                </Stack>
                <LassoGrid
                    columns={globusCollectionColumnDefs}
                    //@ts-ignore
                    data={myData?.DATA}
                    idKey={''}
                    searchable={true}
                    showHide={[]}
                    tab={''}
                    onRowDoubleClicked={onRowDoubleClicked}
                    loading={isFetching}
                />
            </Stack>
            <LassoModal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <Stack gap={4} padding={3}>
                    <Typography fontSize="32px" fontWeight={600}>Add a new directory</Typography>
                    <LassoTextField
                        label={"New Directory Name"}
                        value={newDir}
                        onChange={(e) => setNewDir(e.target.value)}
                    />
                    <Button
                        variant="primary"
                        onClick={createDirectory}
                    >
                        Create Directory
                    </Button>
                </Stack>
            </LassoModal>
            <LassoModal open={isPathModalOpen} onClose={() => setIsModalOpen(false)}>
                <Stack gap={4} padding={3}>
                    <Typography fontSize="32px" fontWeight={600}>Navigate to Directory</Typography>
                    <LassoTextField
                        label={"Enter a new path to navigate to"}
                        value={newPath}
                        onChange={(e) => setNewPath(e.target.value)}
                    />
                    <Button
                        variant="primary"
                        onClick={goToPath}
                    >
                        Go To Path
                    </Button>
                </Stack>
            </LassoModal>
        </>
    );
}

export default GlobusGrid;